import Phaser from 'phaser';
import { DEFAULT_ASSET_PATH } from './assets/manifest.js';
import { playfieldScale, type PlayfieldOptions } from './core/playfield.js';
import { PlayScene } from './scenes/PlayScene.js';
import { PreloadScene } from './scenes/PreloadScene.js';

export type RendererChoice = 'auto' | 'webgl' | 'canvas';

export interface CreateGameOptions {
  /** DOM element (or id) the canvas is added to */
  parent: string | HTMLElement;
  /** logical resolution in pixels – gameplay scales with the height (see WorldScale) */
  width: number;
  height: number;
  /** where and how the game runs inside the canvas (default: the whole canvas, own scenery and HUD) */
  playfield?: PlayfieldOptions;
  /**
   * Edition-specific scenes that run on top of the PlayScene (HUD, overlays).
   * They are started after loading, in this order.
   */
  overlays?: Phaser.Scene[];
  /** Edition-specific scenes rendered below the PlayScene (e.g. the illuminated mural around the game). */
  underlays?: Phaser.Scene[];
  /** folder that holds the shared assets, default `assets/` (relative to the page) */
  assetPath?: string;
  /** Phaser 4 renders with WebGL; Canvas is deprecated and only kept for comparisons */
  renderer?: RendererChoice;
  backgroundColor?: string;
  /** Phaser scale mode, default FIT */
  scaleMode?: Phaser.Scale.ScaleModeType;
  /** extra Phaser config (merged last) */
  phaserConfig?: Partial<Phaser.Types.Core.GameConfig>;
}

export interface GameHandle {
  game: Phaser.Game;
  play: PlayScene;
  /** resolves once all assets are loaded and the scenes are running */
  ready: Promise<void>;
}

/** Creates the Phaser game with the shared core scenes plus the edition's overlay scenes. */
export function createGame(options: CreateGameOptions): GameHandle {
  const play = new PlayScene(options.playfield);
  const overlays = options.overlays ?? [];
  const underlays = options.underlays ?? [];

  let resolveReady!: () => void;
  const ready = new Promise<void>((r) => (resolveReady = r));

  const preload = new PreloadScene({
    assetPath: options.assetPath ?? DEFAULT_ASSET_PATH,
    next: [play, ...underlays, ...overlays].map((s) => s.sys.settings.key),
    // vector assets are rasterised for the size they are shown at: the playfield, not the canvas
    rasterScale: playfieldScale(options.playfield, options.width, options.height),
    onComplete: () => resolveReady(),
  });

  const renderer = options.renderer ?? 'auto';
  const game = new Phaser.Game({
    type: renderer === 'canvas' ? Phaser.CANVAS : renderer === 'webgl' ? Phaser.WEBGL : Phaser.AUTO,
    parent: options.parent,
    width: options.width,
    height: options.height,
    backgroundColor: options.backgroundColor ?? '#000000',
    scale: { mode: options.scaleMode ?? Phaser.Scale.FIT, autoCenter: Phaser.Scale.CENTER_BOTH },
    fps: { target: 60, smoothStep: false },
    render: { antialias: true, powerPreference: 'high-performance' },
    banner: false,
    // order = render order: preload, underlays, game, then the overlays on top
    scene: [preload, ...underlays, play, ...overlays],
    ...options.phaserConfig,
  });

  return { game, play, ready };
}
